import { marked } from 'marked';
import TurndownService from 'turndown';

// Configure marked for parsing markdown
marked.use({
  breaks: true,
  gfm: true,
});

// Configure Turndown for converting HTML to markdown
const turndownService = new TurndownService({
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '_',
  strongDelimiter: '**',
  linkStyle: 'inlined',
});

// Custom rules for TipTap-specific elements
turndownService.addRule('taskListItem', {
  filter: (node) => {
    return (
      node.nodeName === 'LI' &&
      node.parentNode &&
      node.parentNode.nodeName === 'UL' &&
      node.parentNode.getAttribute &&
      node.parentNode.getAttribute('data-type') === 'taskList'
    );
  },
  replacement: (content, node) => {
    const isChecked = node.querySelector('input[type="checkbox"]')?.checked;
    return `- [${isChecked ? 'x' : ' '}] ${content}\n`;
  },
});

turndownService.addRule('mention', {
  filter: (node) => {
    return node.nodeName === 'SPAN' && node.classList?.contains('mention');
  },
  replacement: (content) => {
    return `@${content}`;
  },
});

/**
 * Convert markdown text to HTML
 */
export function markdownToHtml(markdown: string): string {
  try {
    return marked.parse(markdown) as string;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return markdown;
  }
}

/**
 * Convert HTML to markdown
 */
export function htmlToMarkdown(html: string): string {
  try {
    return turndownService.turndown(html);
  } catch (error) {
    console.error('Error converting HTML to markdown:', error);
    return html;
  }
}

/**
 * Convert TipTap JSON to markdown
 */
export function tiptapJsonToMarkdown(json: any): string {
  try {
    // First convert JSON to HTML using a basic renderer
    const html = renderTiptapJsonToHtml(json);
    return htmlToMarkdown(html);
  } catch (error) {
    console.error('Error converting TipTap JSON to markdown:', error);
    return '';
  }
}

/**
 * Convert TipTap JSON to HTML (basic renderer)
 */
function renderTiptapJsonToHtml(doc: any): string {
  if (!doc || !doc.content) return '';

  return doc.content.map((node: any) => renderNode(node)).join('');
}

function renderNode(node: any): string {
  const { type, content, attrs, marks } = node;

  let html = '';

  switch (type) {
    case 'paragraph':
      html = `<p>${content ? content.map(renderNode).join('') : ''}</p>`;
      break;
    case 'heading':
      const level = attrs?.level || 1;
      html = `<h${level}>${content ? content.map(renderNode).join('') : ''}</h${level}>`;
      break;
    case 'bulletList':
      html = `<ul>${content ? content.map(renderNode).join('') : ''}</ul>`;
      break;
    case 'orderedList':
      html = `<ol>${content ? content.map(renderNode).join('') : ''}</ol>`;
      break;
    case 'listItem':
      html = `<li>${content ? content.map(renderNode).join('') : ''}</li>`;
      break;
    case 'taskList':
      html = `<ul data-type="taskList">${content ? content.map(renderNode).join('') : ''}</ul>`;
      break;
    case 'taskItem':
      const checked = attrs?.checked ? ' checked' : '';
      html = `<li><input type="checkbox"${checked}>${content ? content.map(renderNode).join('') : ''}</li>`;
      break;
    case 'blockquote':
      html = `<blockquote>${content ? content.map(renderNode).join('') : ''}</blockquote>`;
      break;
    case 'codeBlock':
      const language = attrs?.language ? ` class="language-${attrs.language}"` : '';
      html = `<pre${language}><code>${content ? content.map(renderNode).join('') : ''}</code></pre>`;
      break;
    case 'image':
      const src = attrs?.src || '';
      const alt = attrs?.alt || '';
      const title = attrs?.title ? ` title="${attrs.title}"` : '';
      html = `<img src="${src}" alt="${alt}"${title}>`;
      break;
    case 'hardBreak':
      html = '<br>';
      break;
    case 'horizontalRule':
      html = '<hr>';
      break;
    case 'text':
      html = node.text || '';
      break;
    case 'mention':
      html = `<span class="mention">${attrs?.label || ''}</span>`;
      break;
    default:
      html = content ? content.map(renderNode).join('') : '';
  }

  // Apply text marks
  if (marks && marks.length > 0) {
    marks.forEach((mark: any) => {
      switch (mark.type) {
        case 'bold':
          html = `<strong>${html}</strong>`;
          break;
        case 'italic':
          html = `<em>${html}</em>`;
          break;
        case 'underline':
          html = `<u>${html}</u>`;
          break;
        case 'strike':
          html = `<s>${html}</s>`;
          break;
        case 'code':
          html = `<code>${html}</code>`;
          break;
        case 'link':
          const href = mark.attrs?.href || '';
          const target = mark.attrs?.target ? ` target="${mark.attrs.target}"` : '';
          html = `<a href="${href}"${target}>${html}</a>`;
          break;
        case 'textStyle':
          if (mark.attrs?.color) {
            html = `<span style="color: ${mark.attrs.color}">${html}</span>`;
          }
          break;
      }
    });
  }

  return html;
}

/**
 * Import markdown content and return TipTap-compatible format
 */
export function importMarkdown(markdown: string): string {
  // Convert markdown to HTML, then let TipTap handle the HTML import
  return markdownToHtml(markdown);
}

/**
 * Export TipTap content as markdown
 */
export function exportAsMarkdown(content: string | object): string {
  if (typeof content === 'string') {
    // Content is HTML
    return htmlToMarkdown(content);
  } else {
    // Content is TipTap JSON
    return tiptapJsonToMarkdown(content);
  }
}

/**
 * Generate a downloadable markdown file
 */
export function downloadMarkdown(content: string | object, filename: string): void {
  const markdown = exportAsMarkdown(content);
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Read markdown file and return content
 */
export function readMarkdownFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const markdown = e.target?.result as string;
      resolve(markdown);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
}